// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import clearEmptyChild from "@toolkit/Framework/Common/Utilities/react/clearEmptyChild";
import { clearfixStyles } from "@toolkit/Themes/common/mixins";
import React, { Children, forwardRef, ForwardRefRenderFunction, PropsWithChildren, useMemo } from "react";
import { css } from "styled-components";

export interface FlexWrapperPresentationProps extends ExpandStyles<FlexWrapperStyles> {
    gap?: string;
    verticalGap?: string;
    itemsPerRow?: Array<number | null>;
}

export interface FlexWrapperProps extends FlexWrapperPresentationProps {
    wrap?: boolean;
}

export interface FlexWrapperStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss & { forwardAs?: keyof JSX.IntrinsicElements };
    item?: InjectableCss & { forwardAs?: keyof JSX.IntrinsicElements };
}

export const flexWrapperGapStyles = (gap?: string, verticalGap?: string) => {
    const _gap = gap || 0;
    const _verticalGap = verticalGap || _gap;

    return css`
        margin: ${_verticalGap} 0 0 ${_gap};
    `;
};

export const flexWrapperGapOffsetStyles = (gap?: string, verticalGap?: string) => {
    return flexWrapperGapStyles(gap ? `-${gap}` : undefined, verticalGap ? `-${verticalGap}` : undefined);
};

export const flexWrapperStyles: FlexWrapperStyles = {
    wrapper: {
        css: css`
            ${clearfixStyles()}
        `,
    },
    content: {
        css: css`
            display: flex;
            flex-flow: row wrap;
            flex: 1 0 100%;
        `,
    },
    item: {
        css: css`
            display: flex;
        `,
    },
};

const FlexWrapper: ForwardRefRenderFunction<HTMLDivElement, PropsWithChildren<FlexWrapperProps>> = (
    {
        gap,
        verticalGap,
        itemsPerRow,
        wrap = true,
        extendedStyles,
        overridedStyles,
        children: baseChildren,
        ...htmlAttributes
    },
    ref,
) => {
    const baseStyles = useExpandProps(flexWrapperStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<FlexWrapperStyles>(
            {
                content: {
                    css: flexWrapperGapOffsetStyles(gap, verticalGap),
                },
                item: {
                    css: css`
                        ${flexWrapperGapStyles(gap, verticalGap)}
                        ${() => {
                            if (itemsPerRow) {
                                return css`
                                    ${breakpointValues(
                                        itemsPerRow.map(x => (x ? `calc(${100 / x}% - ${gap || `0px`})` : null)),
                                        "flex-basis",
                                    )}
                                `;
                            }
                        }}
                    `,
                },
            },
            baseStyles,
        );
    }, [baseStyles, gap, verticalGap, itemsPerRow]);

    const children = clearEmptyChild(baseChildren);
    if (!Children.count(children)) {
        return null;
    }

    return (
        <StyledWrapper ref={ref} {...styles.wrapper} {...htmlAttributes}>
            <StyledWrapper {...styles.content} as={styles.content?.forwardAs}>
                {wrap
                    ? Children.map(children, x =>
                          x ? (
                              <StyledWrapper {...styles.item} as={styles.item?.forwardAs}>
                                  {x}
                              </StyledWrapper>
                          ) : null,
                      )
                    : children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default forwardRef(FlexWrapper);
